import { ESSENTIALS_SELECTION } from './permission-presets.mjs';

/**
 * ENTRAID-350 "start small", ONE rule for the backend and the onboarding wizard (security review of PR 395, M2 and L5):
 * which customers are offered the Default (Essentials) set, and which keep every data source.
 *
 * Only a NEW customer starts small. An ESTABLISHED customer (one that consented, collected, held a collector key on the
 * retired shared app, or carries any later lifecycle history) keeps all data sources, so a reconnect, a cutover, a repair or
 * a lapse and renewal never narrows what it collects without the customer's own choice. When in doubt the answer is
 * "established": keeping every source is the safe direction (it never silently turns off a source the customer relied on).
 *
 * The customer admin's view of the record (lib/customer-public.mjs) carries no key ids or revoke history, so the backend
 * adds the derived `establishedCustomer` flag computed here from the raw record; the wizard honors it.
 */

/** The only statuses a customer that never collected can be in. Any other status (or none) is history. */
export const NEW_CUSTOMER_STATUSES = Object.freeze(['registered', 'consent-pending']);

const nonEmpty = (v) => (Array.isArray(v) ? v.length > 0 : v instanceof Set ? v.size > 0 : false);

/** True when the customer must keep every data source by default (never narrowed to the Default set). */
export function isEstablishedCustomer(customer) {
  if (!customer || typeof customer !== 'object') return false;
  // Derived by the backend from the raw record (the customer view hides the fields below).
  if (customer.establishedCustomer === true) return true;
  if (nonEmpty(customer.consentedPermissions)) return true;
  // The retired shared app: a legacy key, its revocation, or a finished cutover.
  if (customer.legacyAppKeyId || customer.legacyKeyRevokedAt || customer.legacyCutoverFinishedAt) return true;
  // A collector key without an app of its own is a key on the retired shared app, whatever the status.
  const ownApp = Boolean(customer.entraAppObjectId || customer.entraAppClientId);
  if (customer.appCredentialKeyId && !ownApp) return true;
  // Revoke history: a lapsed customer whose key was revoked (or is owed a revoke) stays what it was.
  if (customer.revokeFailedAt || customer.collectorKeyRevokedAt || nonEmpty(customer.pendingRevokeKeyIds)) return true;
  return !NEW_CUSTOMER_STATUSES.includes(customer.status);
}

/** The selection a customer without a recorded one is built from: every source if established, else the Default set. */
export function defaultSelectionFor(customer) {
  return isEstablishedCustomer(customer)
    ? { mode: 'all', sources: [] }
    : { mode: ESSENTIALS_SELECTION.mode, sources: [...ESSENTIALS_SELECTION.sources] };
}

/**
 * Whether onboarding Step 1 asks Default or Customize before the consent link. Only a new customer that has not recorded a
 * selection is asked; a reconnect (entraConsentResetAt) or an established customer goes straight to the consent link.
 */
export function needsDataSourceChoice(customer) {
  if (!customer || customer.permissionSelection) return false;
  if (customer.entraConsentResetAt) return false;
  return !isEstablishedCustomer(customer);
}
